import { makeAutoObservable } from "mobx";
import CallStore from "./CallStore";
import { ToolWidth } from "../helpers/statics";

/**
 * Screenshot store for managing screen capture and drawing functionality
 * Handles screenshot capture, shape drawing, and annotation tools
 */
class screenShotStore {

  /**
   * Initialize the screenshot store
   * Sets up MobX observables for reactive state management
   */
  constructor() {
    makeAutoObservable(this);
  }

  /** @type {AgoraVideoTrack|null} Agora video track for screenshot capture */
  tracker = null;

  /** @type {HTMLCanvasElement|null} Canvas element containing screenshot */
  sc = null;

  /** @type {boolean} Whether screenshot screen is visible */
  showScScreen = false;

  /** @type {string|null} Currently selected drawing shape */
  selectedShape = null;

  /** @type {Array} Array of drawn shapes on screenshot */
  shapes = [];

  /**
   * Set the Agora video track for screenshot capture
   * @param {AgoraVideoTrack} tracker - Video track from Agora RTC
   * @returns {void}
   */
  setTracker(tracker) {
    this.tracker = tracker;
  }

  /**
   * Set screenshot screen visibility state
   * Clears canvas when hiding the screen
   * @param {boolean} show - Whether to show screenshot screen
   * @returns {void}
   */
  setShowScScreen(show) {
    this.showScScreen = show;
    if (!show) {
      this.sc = null;
    }
  }

  /**
   * Set the screenshot canvas element
   * @param {HTMLCanvasElement} sc - Canvas element with screenshot
   * @returns {void}
   */
  setSc(sc) {
    this.sc = sc;
  }

  /**
   * Set the currently selected drawing shape
   * @param {string} shape - Shape type (free-hand, circle, line-up, etc.)
   * @returns {void}
   */
  setSelectedShape(shape) {
    this.selectedShape = shape;
  }

  /**
   * Set the color for drawing shapes
   * @param {string} color - Color value (hex, rgb, etc.)
   * @returns {void}
   */
  setSelectedShapeColor(color) {
    this.selectedShapeColor = color;
  }

  /**
   * Clear all screenshot data and reset store state
   * @returns {void}
   */
  clearStore() {
    this.shapes = [];
    this.sc = null;
    this.showScScreen = false;
    this.selectedShape = null;
  }

  /**
   * Find shape at specific coordinates
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @returns {Object|null} Shape object if found, null otherwise
   */
  getShapeByCoordinates(x, y) {
    return this.shapes.reverse().find(shape =>
      x >= shape.x - ToolWidth && x <= shape.x + ToolWidth &&
      y >= shape.y - ToolWidth && y <= shape.y + ToolWidth
    );
  }

  /**
   * Add a new shape to the screenshot
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @param {string} shape - Shape type
   * @param {string} color - Shape color
   * @returns {void}
   */
  addShape(x, y, shape, color) {
    const id = Math.random().toString(36).substring(2, 15);
    this.shapes.push({
      id,
      shape: shape || 'free-hand',
      color: color || '#000000',
      x: x || 0,
      y: y || 0,
    });
  }

  /**
   * Remove a shape by its ID
   * @param {string} id - Shape ID to remove
   * @returns {void}
   */
  removeShape(id) {
    this.shapes = this.shapes.filter(s => s.id !== id);
  }


  /**
   * Capture screenshot from Agora video track
   * Processes video frame data and creates canvas with proper scaling
   * @async
   * @returns {Promise<void>}
   */
  async takeScreenshot() {
    if (!this.tracker) {
      return;
    }

    const data = await this.tracker.getCurrentFrameData();

    if (!data) return;

    // Original image dimensions
    const sourceWidth = data.width;
    const sourceHeight = data.height;

    // Target (desired) resolution
    const targetWidth = CallStore.customerScreenResolution?.width;
    const targetHeight = CallStore.customerScreenResolution?.height;

    if (!targetWidth || !targetHeight) return;

    // Temporary canvas for imageData processing
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = sourceWidth;
    tempCanvas.height = sourceHeight;
    const tempCtx = tempCanvas.getContext('2d');
    tempCtx.putImageData(data, 0, 0);

    // Main canvas for display
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');

    // Calculate: object-fit: cover effect with proper scale and offset
    const sourceRatio = sourceWidth / sourceHeight;
    const targetRatio = targetWidth / targetHeight;

    let drawWidth, drawHeight, offsetX, offsetY;

    if (sourceRatio > targetRatio) {
      // Wide image, crop horizontally
      drawHeight = targetHeight;
      drawWidth = sourceWidth * (targetHeight / sourceHeight);
      offsetX = -(drawWidth - targetWidth) / 2;
      offsetY = 0;
    } else {
      // Tall image, crop vertically
      drawWidth = targetWidth;
      drawHeight = sourceHeight * (targetWidth / sourceWidth);
      offsetX = 0;
      offsetY = -(drawHeight - targetHeight) / 2;
    }

    // Draw from tempCanvas to target canvas
    ctx.drawImage(tempCanvas, offsetX, offsetY, drawWidth, drawHeight);

    // Save to store
    this.setSc(canvas);
    this.setShowScScreen(true);
  }

}

const ScreenShotStore = new screenShotStore();
export default ScreenShotStore;